import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ThemeProvider from "@/components/ThemeProvider";
import { LanguageProvider } from "@/lib/i18n";
import { SettingsProvider, useSettings } from "@/lib/settingsContext";
import { CurrencyProvider } from "@/lib/currencyContext";
import { useEffect, useState, lazy, Suspense } from "react";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AgeGate from "@/components/AgeGate";
import AdminLayout from "@/components/AdminLayout";
import CabinetLayout from "@/components/CabinetLayout";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import MobileBottomNav from "@/components/MobileBottomNav";

/* Lazy-loaded pages for code-splitting and faster initial load */
const Home = lazy(() => import("@/pages/Home"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const ModelDetail = lazy(() => import("@/pages/ModelDetail"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Services = lazy(() => import("@/pages/Services"));
const About = lazy(() => import("@/pages/About"));
const SearchByParams = lazy(() => import("@/pages/SearchByParams"));
const EscortWork = lazy(() => import("@/pages/EscortWork"));
const Contact = lazy(() => import("@/pages/Contact"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

const CabinetDashboard = lazy(() => import("@/pages/cabinet/Dashboard"));
const CabinetStats = lazy(() => import("@/pages/cabinet/Stats"));
const CabinetBalance = lazy(() => import("@/pages/cabinet/Balance"));
const CabinetSettings = lazy(() => import("@/pages/cabinet/Settings"));
const MyApplications = lazy(() => import("@/pages/cabinet/MyApplications"));
const ModelProfile = lazy(() => import("@/pages/cabinet/ModelProfile"));
const CabinetFavorites = lazy(() => import("@/pages/cabinet/Favorites"));
const CabinetSubscriptions = lazy(() => import("@/pages/cabinet/Subscriptions"));

const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminModelsList = lazy(() => import("@/pages/admin/ModelsList"));
const AdminModelForm = lazy(() => import("@/pages/admin/ModelForm"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminMediaLibrary = lazy(() => import("@/pages/admin/MediaLibrary"));
const AdminApplications = lazy(() => import("@/pages/admin/Applications"));
const AdminServices = lazy(() => import("@/pages/admin/Services"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminAccount = lazy(() => import("@/pages/admin/Account"));
const AdminGuide = lazy(() => import("@/pages/admin/Guide"));
const AdminEmail = lazy(() => import("@/pages/admin/Email"));
const AdminSupport = lazy(() => import("@/pages/admin/Support"));
const AdminAnalytics = lazy(() => import("@/pages/admin/Analytics"));
const AdminBackups = lazy(() => import("@/pages/admin/Backups"));
const AdminModeration = lazy(() => import("@/pages/admin/Moderation"));
const AdminExperiments = lazy(() => import("@/pages/admin/Experiments"));
const AdminTranslations = lazy(() => import("@/pages/admin/Translations"));
const AdminPageContent = lazy(() => import("@/pages/admin/PageContent"));
const AdminNotifications = lazy(() => import("@/pages/admin/Notifications"));

const MaintenancePage = lazy(() => import("@/pages/MaintenancePage"));
const Nginx404Page = lazy(() => import("@/pages/Nginx404Page"));

/* Loading spinner shown during lazy load */
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="relative w-10 h-10">
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[var(--color-primary,#E11D48)] animate-spin" />
        <div className="absolute inset-1 rounded-full border-2 border-transparent border-b-[var(--color-primary,#E11D48)]/40 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
      </div>
    </div>
  );
}

const ADMIN_PATH = "/flvbybcnhfnjh-gfytkm-8246";
const API = process.env.REACT_APP_BACKEND_URL;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  const [settings, setSettings] = useState(null);
  const [ageVerified, setAgeVerified] = useState(() => localStorage.getItem('ageVerified') === 'true');
  const [maintenanceMode, setMaintenanceMode] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(setSettings).catch(() => setSettings({}));
    fetch(`${API}/api/site/status`).then(r => r.json()).then(d => setMaintenanceMode(d.mode || 'off')).catch(() => setMaintenanceMode('off'));
  }, []);

  if (settings === null || maintenanceMode === null) return null;
  if (maintenanceMode === 'maintenance') return <ThemeProvider settings={settings}><MaintenancePage /></ThemeProvider>;
  if (maintenanceMode === 'nginx404') return <Nginx404Page />;

  return (
    <ThemeProvider settings={settings}>
      {!ageVerified && settings?.verificationAge && <AgeGate onConfirm={() => setAgeVerified(true)} />}
      <Header />
      <main className="pt-16 pb-20 lg:pb-0 min-h-[60vh]">{children}</main>
      <Footer />
      <PwaInstallPrompt />
      <MobileBottomNav />
    </ThemeProvider>
  );
}

function RecaptchaGate({ children }) {
  const settings = useSettings();
  const version = (settings?.recaptchaVersion || '').toLowerCase();
  const v3Key = settings?.recaptchaV3SiteKey || process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (version === 'v3' && v3Key) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={v3Key}>
        {children}
      </GoogleReCaptchaProvider>
    );
  }
  return children;
}

function App() {
  return (
    <LanguageProvider>
      <SettingsProvider>
        <RecaptchaGate>
          <CurrencyProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Toaster position="top-right" richColors />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Admin routes - not affected by maintenance */}
                  <Route path={`${ADMIN_PATH}/login`} element={<AdminLogin />} />
                  <Route path={ADMIN_PATH} element={<AdminLayout />}>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="models" element={<AdminModelsList />} />
                    <Route path="models/new" element={<AdminModelForm />} />
                    <Route path="models/:id/edit" element={<AdminModelForm />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="media" element={<AdminMediaLibrary />} />
                    <Route path="applications" element={<AdminApplications />} />
                    <Route path="services" element={<AdminServices />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="account" element={<AdminAccount />} />
                    <Route path="guide" element={<AdminGuide />} />
                    <Route path="email" element={<AdminEmail />} />
                    <Route path="support" element={<AdminSupport />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="backups" element={<AdminBackups />} />
                    <Route path="moderation" element={<AdminModeration />} />
                    <Route path="experiments" element={<AdminExperiments />} />
                    <Route path="translations" element={<AdminTranslations />} />
                    <Route path="page-content" element={<AdminPageContent />} />
                    <Route path="notifications" element={<AdminNotifications />} />
                  </Route>

                  {/* Public routes with header/footer and maintenance gate */}
                  <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                  <Route path="/modelki" element={<PublicLayout><Catalog /></PublicLayout>} />
                  <Route path="/modelki/:slug" element={<PublicLayout><ModelDetail /></PublicLayout>} />
                  <Route path="/miasta/:city" element={<PublicLayout><CityPage /></PublicLayout>} />
                  <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
                  <Route path="/uslugi" element={<PublicLayout><Services /></PublicLayout>} />
                  <Route path="/o-nas" element={<PublicLayout><About /></PublicLayout>} />
                  <Route path="/szukaj" element={<PublicLayout><SearchByParams /></PublicLayout>} />
                  <Route path="/szukaj-po-parametrach" element={<PublicLayout><SearchByParams /></PublicLayout>} />
                  <Route path="/praca" element={<PublicLayout><EscortWork /></PublicLayout>} />
                  <Route path="/kontakt" element={<PublicLayout><Contact /></PublicLayout>} />
                  <Route path="/polityka-prywatnosci" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
                  <Route path="/regulamin" element={<PublicLayout><Terms /></PublicLayout>} />
                  <Route path="/logowanie" element={<PublicLayout><Login /></PublicLayout>} />
                  <Route path="/rejestracja" element={<PublicLayout><Register /></PublicLayout>} />
                  <Route path="/potwierdzenie-email" element={<PublicLayout><VerifyEmail /></PublicLayout>} />
                  <Route path="/zapomnialem-hasla" element={<PublicLayout><ForgotPassword /></PublicLayout>} />
                  <Route path="/reset-hasla" element={<PublicLayout><ResetPassword /></PublicLayout>} />

                  <Route path="/konto" element={<PublicLayout><CabinetLayout /></PublicLayout>}>
                    <Route index element={<CabinetDashboard />} />
                    <Route path="ankiety" element={<MyApplications />} />
                    <Route path="model-profile" element={<ModelProfile />} />
                    <Route path="favorites" element={<CabinetFavorites />} />
                    <Route path="subscriptions" element={<CabinetSubscriptions />} />
                    <Route path="statystyki" element={<CabinetStats />} />
                    <Route path="balans" element={<CabinetBalance />} />
                    <Route path="ustawienia" element={<CabinetSettings />} />
                  </Route>

                  <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </CurrencyProvider>
        </RecaptchaGate>
      </SettingsProvider>
    </LanguageProvider>
  );
}

export default App;
